// PageDescription: 의사 상세 페이지
import DefaultLayout from '@/components/layouts/DefaultLayout';
import useGetDoctorByUser from '@/hooks/doctor/useGetDoctorByUser';
import { parseContactTypeOf } from '@/parsers/common';
import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';
import { CustomPageProps } from '@/types/app';
import { TelemedicineTargetType } from '@/types/bff/gql-schema';
import { EDiagnosisContactType } from '@/types/diagnosis';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { DoctorDetailTemplate } from '@/views/doctor/detail';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';

interface DoctorDetailPageProps extends CustomPageProps {
  seoData?: {
    title: string;
    description: string;
    keywords: string;
  };
}

const DoctorDetailPage = ({ client, seoData }: DoctorDetailPageProps) => {
  return (
    <>
      {seoData && (
        <NextSeo
          title={seoData.title}
          description={seoData.description}
          additionalMetaTags={[
            {
              name: 'keywords',
              content: seoData.keywords,
            },
          ]}
        />
      )}
      <DoctorDetailTemplate client={client} />
    </>
  );
};

export default DoctorDetailPage;

DoctorDetailPage.Layout = DefaultLayout;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (context) => {
  const { params, query } = context;
  const queryClient = new QueryClient();

  if (context.client.isWebView) {
    return {
      props: {},
    };
  }

  const doctorId = params?.doctorId as string;
  const contactType = (query?.contactType as EDiagnosisContactType) || EDiagnosisContactType.NonF2F;
  const diagnosisType = (query?.type as ECategoryType) || ECategoryType.Department;
  const telemedicineEligibleTargetType = TelemedicineTargetType.AlwaysEligible;

  if (!doctorId) {
    return {
      notFound: true,
    };
  }

  try {
    const queryParams = {
      doctorPublicId: doctorId,
      telemedicineEligibleTargetType,
      contactType: parseContactTypeOf(contactType),
      isSelectedVaccination: diagnosisType === ECategoryType.Vaccination,
    };

    const doctorDetail = await useGetDoctorByUser.fetchQuery(queryClient, queryParams, {
      contactType,
    });

    let seoData = {
      title: '의사 상세 정보 | 비대면진료 - 닥터나우',
      description: '의사 선생님의 프로필과 진료시간을 확인하고 비대면진료를 예약해 보세요.',
      keywords: '비대면진료, 의사, 진료예약, 닥터나우',
    };

    if (doctorDetail?.doctor && doctorDetail?.hospital) {
      const hospitalName = doctorDetail.hospital.name || '';
      const doctorName = doctorDetail.doctor.name || '';

      if (hospitalName && doctorName) {
        seoData = {
          title: `${hospitalName} ${doctorName} 비대면진료 예약`,
          description: `${hospitalName} ${doctorName} 선생님의 프로필과 진료시간을 확인하고 비대면진료를 예약해 보세요.`,
          keywords: `${hospitalName}, ${doctorName}, 비대면진료, 닥터나우`,
        };
      }
    }

    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        seoData,
      },
    };
  } catch {
    const defaultSeoData = {
      title: '의사 상세 정보 | 비대면진료 - 닥터나우',
      description: '의사 선생님의 프로필과 진료시간을 확인하고 비대면진료를 예약해 보세요.',
      keywords: '비대면진료, 의사, 진료예약, 닥터나우',
    };

    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        seoData: defaultSeoData,
      },
    };
  }
});
